import { useCallback, useEffect, useRef, useState } from 'react';
import {
  approveQuestion,
  listQuestions,
  rejectQuestion,
} from '../services/questions';
import { ApiError } from '@admin/services/api';
import { useToastStore } from '@admin/stores/toastStore';
import ReviewCard from './ReviewCard';
import type { QuestionListItem, QuestionsListResponse } from '@games/word-clash/shared/types';

const PAGE_SIZE = 20;

/**
 * Question Review queue.
 *
 * Walks the admin through every question that hasn't yet been reviewed
 * (reviewed != true, which includes legacy rows missing the field). For each
 * card the admin clicks Approve (mark reviewed) or Reject (mark reviewed and
 * deactivate). Both actions optimistically remove the card from the queue;
 * a failed request rolls the card back and surfaces a toast.
 *
 * Pagination is a simple "Load more" — we don't keep a page index because
 * the queue shrinks as the admin works through it (the next 20 unreviewed
 * rows are always at "page 1" once the current batch is cleared).
 */
export default function QuestionReviewPage() {
  const showToast = useToastStore((s) => s.showToast);

  const [items, setItems] = useState<QuestionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Cancel-stale-response guard so a slow initial fetch doesn't clobber a
  // later one (e.g., the user hits "Load more" twice quickly).
  const reqCounterRef = useRef(0);

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean) => {
      const reqId = ++reqCounterRef.current;
      setState('loading');
      try {
        const data: QuestionsListResponse = await listQuestions({
          page: targetPage,
          pageSize: PAGE_SIZE,
          reviewed: false,
        });
        if (reqId !== reqCounterRef.current) return;
        // Dedupe by id when appending — under heavy approval activity the
        // server-side offset may overlap with rows already in view because
        // reviewing a row removes it from the unreviewed set and shifts
        // subsequent rows up one position.
        setItems((prev) => {
          if (!append) return data.data;
          const seen = new Set(prev.map((q) => q.id));
          return [...prev, ...data.data.filter((q) => !seen.has(q.id))];
        });
        setTotal(data.total);
        setHasMore(data.hasMore);
        setState('ready');
      } catch (err) {
        if (reqId !== reqCounterRef.current) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load review queue';
        setErrorMessage(message);
        setState('error');
      }
    },
    [],
  );

  useEffect(() => {
    void fetchPage(1, false);
  }, [fetchPage]);

  function loadMore() {
    const next = page + 1;
    setPage(next);
    void fetchPage(next, true);
  }

  function handleUpdated(updated: QuestionListItem) {
    setItems((prev) =>
      prev.map((q) => (q.id === updated.id ? updated : q)),
    );
  }

  async function handleReview(id: string, action: 'approve' | 'reject') {
    if (pendingId) return; // serialize button clicks per card
    setPendingId(id);

    // Optimistic remove — keep a copy so we can roll back on error.
    const snapshot = items;
    setItems((prev) => prev.filter((q) => q.id !== id));
    setTotal((t) => Math.max(0, t - 1));

    try {
      if (action === 'approve') {
        await approveQuestion(id);
      } else {
        await rejectQuestion(id);
      }
      showToast(
        action === 'approve'
          ? 'Question approved'
          : 'Question rejected (hidden from game)',
        'success',
      );
    } catch (err) {
      // Roll back the optimistic update so the admin can retry.
      setItems(snapshot);
      setTotal((t) => t + 1);
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Review action failed';
      showToast(message, 'error');
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-4" data-testid="question-review-page">
      <header>
        <h1 className="text-2xl font-semibold">Question Review</h1>
        <p className="text-sm text-zinc-500">
          Walk through every unreviewed question. Approve to mark it reviewed;
          Reject to mark reviewed and hide it from the in-game pool.
        </p>
      </header>

      {state === 'error' ? (
        <div
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          data-testid="review-error"
        >
          {errorMessage}
          <button
            type="button"
            onClick={() => {
              setPage(1);
              void fetchPage(1, false);
            }}
            className="ml-2 underline"
            data-testid="review-retry"
          >
            Retry
          </button>
        </div>
      ) : null}

      {state === 'loading' && items.length === 0 ? (
        <div
          className="text-sm text-zinc-500"
          data-testid="review-loading"
        >
          Loading…
        </div>
      ) : null}

      {state !== 'loading' && items.length === 0 && state !== 'error' ? (
        <div
          className="rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
          data-testid="review-empty"
        >
          Nothing left to review. Every question has been triaged.
        </div>
      ) : null}

      {items.length > 0 ? (
        <div className="text-xs text-zinc-500" data-testid="review-counter">
          {total.toLocaleString()} questions still awaiting review
        </div>
      ) : null}

      <ul className="space-y-3" data-testid="review-list">
        {items.map((q) => (
          <ReviewCard
            key={q.id}
            question={q}
            disabled={pendingId === q.id}
            onApprove={() => void handleReview(q.id, 'approve')}
            onReject={() => void handleReview(q.id, 'reject')}
            onUpdated={handleUpdated}
          />
        ))}
      </ul>

      {hasMore && state === 'ready' ? (
        <div>
          <button
            type="button"
            onClick={loadMore}
            data-testid="review-load-more"
            className="rounded border border-zinc-300 px-4 py-1.5 text-sm hover:bg-zinc-50"
          >
            Load more
          </button>
        </div>
      ) : null}
    </div>
  );
}
